<template>
	<div class="pointer-events-none">
		<div
			v-for="(group, position) in groupedBadges"
			:key="position"
			:class="[getPositionClass(position as BadgePosition), {'grayscale': isInStock(product) === false}]"
			class="flex flex-col gap-3"
		>
			<ProductBadge
				v-for="badgeName in group"
				:key="badgeName"
				:badge="String(badgeName)"
				:age-limitation="ageLimitation"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import {groupWith} from '@dovca/toolbox';
	import type {Gsoa_ProductCatalog_Badge as ProductCatalogBadge, Gsoa_ProductCatalog_ProductDetail as ProductDetail} from '~/openapi';
	import {Badge, type BadgePosition} from '~/types/product';
	import {isInStock} from '~/utils';

	type Props = {
		product: ProductDetail;
		badges: Partial<Record<Badge, ProductCatalogBadge>>;
		ageLimitation?: number | null;
		positionRemapping?: Partial<Record<BadgePosition, BadgePosition>>;
	};

	const badgePositions: Record<Badge, BadgePosition> = {
		[Badge.MORE_VARIANTS]: 'bottom-left',
		[Badge.CZ_PRODUCT]: 'top-right',
		[Badge.AGE_LIMIT]: 'top-right',
		[Badge.SECURITY_TAG]: 'top-right',
	};

	const positionClasses: Record<BadgePosition, string> = {
		'bottom-left': 'absolute bottom-0 left-0',
		'top-right': 'absolute top-0 right-0',
		'bottom-right': 'absolute bottom-0 right-0 max-md:bottom-1/2 max-md:translate-y-1/2',
	};

	const props = defineProps<Props>();

	const groupedBadges = computed(() => {
		const visible = (Object.entries(props.badges) as [Badge, ProductCatalogBadge | undefined][])
			.filter((entry): entry is [Badge, ProductCatalogBadge] => !!entry[1]?.display)
			.map(([name]) => name);

		return groupWith((name: Badge) => badgePositions[name])(visible);
	});

	function getPositionClass(position: BadgePosition): string {
		const resolvedPosition = props.positionRemapping?.[position] ?? position;
		return positionClasses[resolvedPosition];
	}
</script>
